/**
 * Compact card showing a delegation event between agents.
 * Displays the supervisor agent and target agent list with completion status.
 */

import { memo, type ReactNode, useState } from 'react'

import { ChatAgentItemLayout } from './chat-agent-item-layout.atom'
import { ChatDelegationHeader } from './chat-delegation-header.atom'
import { ChatDelegationReport } from './chat-delegation-report.atom'
import type { ChatDelegation } from './chat-message.interface'

export interface ChatDelegationCardProps {
  delegation: ChatDelegation
  className?: string
}

const resolveTargetLabel = (delegation: ChatDelegation): string =>
  delegation.targetLabelList.length > 0
    ? delegation.targetLabelList.join(', ')
    : delegation.targetList.join(', ')

const resolveBodyContent = (delegation: ChatDelegation): string => delegation.report ?? ''

const ChatDelegationCardBase: React.FC<ChatDelegationCardProps> = ({
  delegation,
  className,
}): ReactNode => {
  const [isExpanded, setIsExpanded] = useState(false)
  const targetLabel = resolveTargetLabel(delegation)
  const bodyContent = resolveBodyContent(delegation)
  const hasContent = bodyContent.length > 0
  const handleToggle = (): void => setIsExpanded((prev) => !prev)

  return (
    <ChatAgentItemLayout
      itemId={delegation.id}
      timestamp={delegation.timestamp}
      agentName={delegation.supervisorId}
      wrapperClassName="chat-delegation-card"
      cardClassName="chat-delegation-card-inner flex-col gap-2 text-left"
      className={className}
      onClick={hasContent ? handleToggle : undefined}
    >
      <ChatDelegationHeader
        targetLabel={targetLabel}
        isComplete={delegation.isComplete}
        hasReport={hasContent}
        isExpanded={isExpanded}
      />
      {hasContent && isExpanded && (
        <ChatDelegationReport content={bodyContent} isStreaming={!delegation.isComplete} />
      )}
    </ChatAgentItemLayout>
  )
}

export const ChatDelegationCard = memo(ChatDelegationCardBase)

ChatDelegationCard.displayName = 'ChatDelegationCard'
